import { AssignmentStatus } from '../../types/assignment';
import { JobAttachment, JobComment, JobStatusChange } from './queries';
import { HistoryItem } from '../Timeline/types';
import { IconType } from '../Icon/Icon';
import Icon from '../Icon/Icon';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import colors from '../../theme/colors';
import React, { useId } from 'react';
import { Box } from 'rebass';
import ActivityActionTooltip, {
  getActivityActionTooltipId,
} from '../ActivityFeed/ActivityActionTooltip';

const WaterSupplierVisibilityIcon = ({
  waterSupplierName,
}: {
  waterSupplierName: string;
}) => {
  const tooltipId = getActivityActionTooltipId(useId());
  const tooltipText = `Visible to ${waterSupplierName}`;

  return React.createElement(
    Box,
    {
      'data-tooltip-id': tooltipId,
      'data-tooltip-content': tooltipText,
      'aria-label': tooltipText,
      sx: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 0,
        color: colors.base.gray[500],
      },
    },
    React.createElement(Icon, { icon: 'eye', size: '12' }),
    React.createElement(ActivityActionTooltip, { id: tooltipId })
  );
};

export const convertJobComment = (
  { id, created, profile, type, comment, visibility }: JobComment,
  waterSupplierName?: string | null,
  waterSupplierProfileId?: string | null
): HistoryItem<'COMMENT' | 'APPROVAL'> => {
  let title: string | undefined = undefined;
  let historyType: 'COMMENT' | 'APPROVAL' = 'COMMENT';
  let icon: IconType = 'chat-dots';
  let iconColor: string | undefined = undefined;

  if (['APPROVAL', 'REJECTION'].includes(type)) {
    historyType = 'APPROVAL';
    if (type === 'APPROVAL' && comment?.toUpperCase() === 'APPROVED') {
      title = 'Approved';
      icon = 'check';
      iconColor = colors.statusSuccess;
    } else {
      title = 'Rejected';
      icon = 'x';
      iconColor = colors.statusError;
    }
    if (['APPROVED', 'REJECTED'].includes(comment?.toUpperCase() ?? '')) {
      comment = '';
    }
  }

  return {
    id,
    created: created!,
    profile: profile,
    type: historyType,
    comment: comment || undefined,
    title,
    icon,
    iconColor,
    adornment:
      visibility === 'EVERYONE' &&
      waterSupplierName &&
      `${profile.id}` !== `${waterSupplierProfileId}`
        ? React.createElement(WaterSupplierVisibilityIcon, {
            waterSupplierName,
          })
        : undefined,
  };
};

export const getJobStatusIcon = (jobStatus: AssignmentStatus): IconType => {
  switch (jobStatus) {
    case 'NEW':
      return 'file';
    case 'ASSIGNED':
      return 'person';
    case 'CANCELED':
    case 'CANCELED_REASSIGNED':
      return 'x';
    case 'FOLLOW_UP':
      return 'repeat';
    case 'COMPLETED':
      return 'check';
    case 'SUBMITTED':
      return 'mail';
  }
  return 'edit';
};

export const convertJobStatusChange = ({
  id,
  created,
  profile,
  toStatus,
}: JobStatusChange): HistoryItem<'STATUS_CHANGE'> => ({
  id,
  created: created!,
  profile,
  type: 'STATUS_CHANGE',
  title: constantToLabel(toStatus),
  icon: getJobStatusIcon(toStatus),
});

export const convertJobAttachment = ({
  id,
  created,
  profile,
  url,
  thumb,
  contentType,
}: JobAttachment): HistoryItem<'ATTACHMENT'> => ({
  id,
  created: created!,
  profile: profile!,
  type: 'ATTACHMENT',
  url,
  thumb: thumb ?? undefined,
  contentType: contentType ?? undefined,
  icon: contentType?.startsWith('image/') ? 'image' : 'file',
});
